// 在线报修表单（手机模式）—— iOS 截图复刻
// 路由：/m/repair?storeId=&storeName=&storePhone=
import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Alert, App as AntdApp, Button, Input, Typography } from 'antd';
import { LeftOutlined, RightOutlined, PlusOutlined } from '@ant-design/icons';
import { createRepair } from '../api/repair.js';
import { uploadImage } from '../api/upload.js';
import StoreSelector from '../components/StoreSelector.js';
import { useGlobalStore } from '../stores/global-store.js';
import { useRegion } from '../hooks/useRegion.js';

const PAGE_BG = '#fff';
const PRIMARY = '#FF7043';

interface PendingRepairImage {
	file: File;
	preview: string;
}

export default function RepairForm() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const region = useRegion();
	const { message } = AntdApp.useApp();
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const [params] = useSearchParams();
	const paramStoreId = params.get('storeId') ?? '';
	const paramStoreName = params.get('storeName') ?? '';
	const paramStorePhone = params.get('storePhone') ?? '';
	const currentStoreId = useGlobalStore((s) => s.currentStoreId);
	const currentStore = useGlobalStore((s) => s.currentStore);
	const storeId = paramStoreId || currentStore?.store_id || currentStoreId || '';
	const storeName = paramStoreName || currentStore?.store || '';
	const storePhone = paramStorePhone || currentStore?.store_phone || currentStore?.phone || currentStore?.mobile || currentStore?.tel || '';
	const fixedStore = Boolean(paramStoreId);

	const [contactUser, setContactUser] = useState('');
	const [contactPhone, setContactPhone] = useState('');
	const [describe, setDescribe] = useState('');
	const [images, setImages] = useState<PendingRepairImage[]>([]);
	const [submitting, setSubmitting] = useState(false);

	const onSubmit = async () => {
		if (!contactUser.trim()) return message.warning(t('repair.contact_required'));
		if (!contactPhone.trim()) return message.warning(t('repair.phone_required'));
		if (!storeId) return message.warning(t('common.select_store'));
		if (!describe.trim()) return message.warning(t('repair.describe_required'));
		setSubmitting(true);
		try {
			const imageUrls: string[] = [];
			if (region === 'apac') {
				for (const image of images) {
					const uploaded = await withSubmitTimeout(uploadImage(image.file, 'repair'), 30000);
					if (!uploaded.ok || !uploaded.url) {
						message.error(uploaded.msg || t('native.common.operation_failed'));
						return;
					}
					imageUrls.push(uploaded.url);
				}
			}
			const r = await withSubmitTimeout(createRepair({
				region,
				store_id: storeId,
				contact_user: contactUser.trim(),
				contact_phone: contactPhone.trim(),
				store_phone: storePhone,
				describe: describe.trim(),
				images: imageUrls,
			}), 45000);
			if (r.ok) {
				message.success(t('repair.success'));
				navigate(-1);
			} else {
				message.error(`${t('repair.failed')}: ${localizeRepairError(r.msg, t)}`);
			}
		} catch (error) {
			message.error(error instanceof Error ? error.message : t('native.common.operation_failed'));
		} finally {
			setSubmitting(false);
		}
	};

	const onPickImages = async (files: FileList | null) => {
		if (!files?.length) return;
		const picked = Array.from(files).slice(0, 5 - images.length);
		const previews = await Promise.all(picked.map(async (file) => ({ file, preview: await readFileDataUrl(file) })));
		setImages((prev) => prev.concat(previews).slice(0, 5));
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	if (region === 'apac') {
		return (
			<div style={{ background: '#fff', minHeight: '100vh' }}>
				<div style={{ maxWidth: 720, margin: '0 auto' }}>
					<div style={{ display: 'flex', alignItems: 'center', height: 48, padding: '0 8px', background: '#fff', borderBottom: '1px solid #eeeeee' }}>
						<Button type="text" shape="circle" icon={<LeftOutlined />} onClick={() => navigate(-1)} />
						<Typography.Text strong style={{ flex: 1, textAlign: 'center', fontSize: 17 }}>
							{t('native.profile.online_repair')}
						</Typography.Text>
						<Button type="text" loading={submitting} onClick={onSubmit} style={{ color: PRIMARY, fontWeight: 500, minWidth: 64 }}>
							{t('native.common.submit')}
						</Button>
					</div>

					<div style={{ borderTop: '10px solid #f5f5f5' }}>
						<NativeRepairRow label={t('native.repair.select_store')}>
							{fixedStore ? (
								<Typography.Text>{storeName || t('common.select_store')}</Typography.Text>
							) : (
								<StoreSelector style={{ width: '100%' }} />
							)}
						</NativeRepairRow>
					</div>
					<div style={{ borderTop: '10px solid #f5f5f5' }}>
						<NativeRepairRow label={t('native.repair.contact_person')}>
							<Input
								value={contactUser}
								onChange={(e) => setContactUser(e.target.value)}
								placeholder={t('repair.optional')}
								variant="borderless"
								style={{ textAlign: 'right', padding: 0 }}
							/>
						</NativeRepairRow>
						<NativeRepairRow label={t('native.repair.contact_number')}>
							<Input
								value={contactPhone}
								onChange={(e) => setContactPhone(e.target.value)}
								placeholder={t('repair.optional')}
								variant="borderless"
								inputMode="tel"
								style={{ textAlign: 'right', padding: 0 }}
							/>
						</NativeRepairRow>
						<NativeRepairRow label={t('native.repair.tel')}>
							<Typography.Text>{storePhone}</Typography.Text>
						</NativeRepairRow>
					</div>

					<div style={{ borderTop: '10px solid #f5f5f5', padding: '14px 12px 12px' }}>
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
							<Typography.Text style={{ color: '#20242b', fontSize: 16 }}>{t('native.repair.fault_description')}</Typography.Text>
							<Typography.Text type="secondary" style={{ fontSize: 12 }}>{describe.length}/300</Typography.Text>
						</div>
						<Input.TextArea
							value={describe}
							onChange={(e) => setDescribe(e.target.value.slice(0, 300))}
							rows={4}
							maxLength={300}
							variant="borderless"
							style={{ padding: 0 }}
						/>
						<div style={{ color: '#b6b6b6', fontSize: 12, lineHeight: 1.5, marginTop: 6 }}>
							{t('native.repair.fault_tip')}
						</div>
					</div>

					<div style={{ borderTop: '10px solid #f5f5f5', padding: '14px 12px 18px' }}>
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
							<Typography.Text style={{ color: '#20242b', fontSize: 16 }}>{t('native.repair.add_picture')}</Typography.Text>
							<Typography.Text type="secondary" style={{ fontSize: 12 }}>{images.length}/5</Typography.Text>
						</div>
						<input
							ref={fileInputRef}
							type="file"
							accept="image/*"
							multiple
							style={{ display: 'none' }}
							onChange={(event) => void onPickImages(event.currentTarget.files)}
						/>
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
							{images.map((image, index) => (
								<button
									key={`${image.file.name}-${image.file.lastModified}-${index}`}
									type="button"
									onClick={() => setImages((prev) => prev.filter((_, i) => i !== index))}
									style={{ width: 88, height: 88, border: 0, padding: 0, background: '#f5f5f5', overflow: 'hidden' }}
									aria-label={t('native.common.remove_picture')}
								>
									<img src={image.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
								</button>
							))}
							{images.length < 5 && (
								<button
									type="button"
									onClick={() => fileInputRef.current?.click()}
									style={{
										width: 88,
										height: 88,
										border: 0,
										background: '#f5f5f5',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										cursor: 'pointer',
									}}
									aria-label={t('native.repair.add_picture')}
								>
									<PlusOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
								</button>
							)}
						</div>
						<div style={{ color: '#b6b6b6', fontSize: 12, lineHeight: 1.5, marginTop: 10 }}>
							{t('native.repair.pictures_tip')}
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div style={{ background: PAGE_BG, minHeight: '100vh' }}>
		<div style={{ maxWidth: 720, margin: '0 auto' }}>
			{/* Header */}
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 12px', borderBottom: '1px solid #f0f0f0' }}>
				<Button type="text" shape="circle" icon={<LeftOutlined />} onClick={() => navigate(-1)} />
				<Typography.Text strong style={{ fontSize: 17 }}>{t('repair.title')}</Typography.Text>
				<Button shape="round" type="primary" loading={submitting} onClick={onSubmit}>
					{t('repair.submit')}
				</Button>
			</div>

			{fixedStore ? (
				<Row label={t('repair.store')} value={storeName} hideArrow />
			) : (
				<Row label={t('repair.store')} hideArrow>
					<StoreSelector style={{ width: '100%' }} />
				</Row>
			)}
			<Row label={t('repair.contact')}>
				<Input
					value={contactUser}
					onChange={(e) => setContactUser(e.target.value)}
					placeholder={t('repair.optional')}
					variant="borderless"
					style={{ textAlign: 'right', padding: 0 }}
				/>
			</Row>
			<Row label={t('repair.contact_phone')}>
				<Input
					value={contactPhone}
					onChange={(e) => setContactPhone(e.target.value)}
					placeholder={t('repair.optional')}
					variant="borderless"
					inputMode="tel"
					style={{ textAlign: 'right', padding: 0 }}
				/>
			</Row>
			<Row label={t('repair.store_phone')} value={storePhone || '—'} hideArrow />

			{/* 故障描述 */}
			<div style={{ padding: '14px 12px 4px', borderTop: '8px solid #f5f5f5' }}>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
					<Typography.Text strong>{t('repair.describe')}</Typography.Text>
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>{describe.length}/300</Typography.Text>
				</div>
				<Input.TextArea
					value={describe}
					onChange={(e) => setDescribe(e.target.value.slice(0, 300))}
					rows={4}
					maxLength={300}
					variant="borderless"
					style={{ padding: 0 }}
				/>
			</div>

			<div style={{ padding: '8px 12px 16px', color: '#bfbfbf', fontSize: 12 }}>
				{t('repair.tip')}
			</div>

			{/* 添加图片占位（实际上传需要 cos 上传逻辑，先 stub） */}
			<div style={{ padding: '14px 12px', borderTop: '8px solid #f5f5f5' }}>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
					<Typography.Text strong>{t('repair.add_images')}</Typography.Text>
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>0/5</Typography.Text>
				</div>
				<Alert
					type="warning"
					showIcon
					message={t('repair.image_upload_pending', {
						defaultValue: '报修图片上传接口暂未接入，当前提交不会上传图片。',
					})}
					style={{ marginBottom: 12 }}
				/>
				<div
					onClick={() => message.info(t('repair.image_upload_pending', {
						defaultValue: '报修图片上传接口暂未接入，当前提交不会上传图片。',
					}))}
					style={{
						width: 88,
						height: 88,
						background: '#f5f5f5',
						borderRadius: 6,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						cursor: 'pointer',
					}}
				>
					<PlusOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
				</div>
				<Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
					{t('repair.images_tip')}
				</Typography.Text>
			</div>

			<div style={{ height: 24 }} />
		</div>
		</div>
	);
}

function NativeRepairRow({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div style={{ display: 'flex', alignItems: 'center', minHeight: 58, padding: '0 12px', borderBottom: '1px solid #eeeeee' }}>
			<Typography.Text style={{ minWidth: 138, color: '#20242b', fontSize: 16 }}>
				{label}
			</Typography.Text>
			<div style={{ flex: 1, minWidth: 0, textAlign: 'right', color: '#555' }}>
				{children}
			</div>
		</div>
	);
}

function withSubmitTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
	const timerHost = typeof window !== 'undefined' ? window : globalThis;
	let timer: ReturnType<typeof timerHost.setTimeout> | undefined;
	const timeout = new Promise<never>((_, reject) => {
		timer = timerHost.setTimeout(() => reject(new Error('Request timeout')), timeoutMs);
	});
	return Promise.race([promise, timeout]).then(
		(value) => {
			if (timer) timerHost.clearTimeout(timer);
			return value;
		},
		(error) => {
			if (timer) timerHost.clearTimeout(timer);
			throw error;
		},
	);
}

function localizeRepairError(msg: string | undefined, t: TFunction): string {
	if (!msg) return t('native.common.operation_failed');
	if (/repair\s+over\s+limit/i.test(msg)) return t('repair.over_limit');
	return msg;
}

function readFileDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(file);
	});
}

function Row({ label, value, children, hideArrow }: { label: string; value?: string; children?: React.ReactNode; hideArrow?: boolean }) {
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				padding: '14px 12px',
				borderBottom: '1px solid #f0f0f0',
			}}
		>
			<Typography.Text strong style={{ minWidth: 88 }}>{label}</Typography.Text>
			<div style={{ flex: 1, textAlign: 'right' }}>
				{children ?? <Typography.Text type="secondary">{value}</Typography.Text>}
			</div>
			{!hideArrow && !children && <RightOutlined style={{ color: '#bfbfbf', marginInlineStart: 4, fontSize: 12 }} />}
			{!children && hideArrow && value && <RightOutlined style={{ color: '#bfbfbf', marginInlineStart: 4, fontSize: 12, opacity: 0 }} />}
		</div>
	);
}
void PRIMARY;
